import * as echarts from 'echarts';
import dayjs, { Dayjs } from 'dayjs';
import { Point } from './type';

const unitNameTextStyle = { color: 'rgba(228, 244, 255, 0.72)', fontSize: 12 };
const hiddenAxisLine = { show: false };
const hiddenAxisTick = { show: false };

const getTooltipValue = (value: unknown) => {
  const raw = Array.isArray(value) ? value[value.length - 1] : value;

  if (raw == null) {
    return '--';
  }

  // 如果是数值类型，保留2位小数
  if (typeof raw === 'number') {
    return raw.toFixed(2);
  }

  return raw;
};

const formatMinuteOfDay = (value: number | string) => {
  const minuteOfDay = Number(value);
  const hour = Math.floor(minuteOfDay / 60);
  const minute = minuteOfDay % 60;

  return `${hour}:${String(minute).padStart(2, '0')}`;
};

const getTooltip = (unitMap: Record<string, string> = {}) => ({
  trigger: 'axis',
  confine: true,
  formatter(items: any[]) {
    const list = Array.isArray(items) ? items : [items];
    const rawTitle = list[0]?.axisValue ?? list[0]?.axisValueLabel ?? list[0]?.name ?? '';
    const title = Number.isFinite(Number(rawTitle)) ? formatMinuteOfDay(rawTitle) : rawTitle;
    const rows = list
      .map(item => {
        const unit = unitMap[item.seriesName] ?? '';
        return `
          <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;line-height:22px;">
            <span style="display:flex;align-items:center;min-width:0;color:var(--mx-text-secondary-color);">
              ${item.marker ?? ''}
              <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${item.seriesName}</span>
            </span>
            <span style="flex-shrink:0;color:var(--mx-text-base-color);">${getTooltipValue(item.value)}${unit}</span>
          </div>
        `;
      })
      .join('');

    return `
      <div style="width:220px;box-sizing:border-box;">
        <div style="margin-bottom:4px;color:var(--mx-text-base-color);line-height:22px;">${title}</div>
        ${rows}
      </div>
    `;
  },
});

const parseMinuteOfDay = (time: string) => {
  const [hour, minute] = time.split(':').map(Number);

  return hour * 60 + minute;
};

const timeValueXAxis = {
  type: 'value',
  min: 0,
  max: 24 * 60,
  minInterval: 60,
  axisTick: { show: false },
  axisLine: hiddenAxisLine,
  axisLabel: {
    formatter: formatMinuteOfDay,
    hideOverlap: true,
  },
  splitLine: { show: false },
};

const toTimedValues = (points: Point[]) => points.map(item => [parseMinuteOfDay(item.time), item.value]);

interface AreaLineOptionConfig {
  areaEndColor?: string;
  areaStartColor?: string;
  lineWidth?: number;
  seriesName?: string;
  showLegend?: boolean;
}

export const getAreaLineOption = (data: Point[], color: string, unit = '', config: AreaLineOptionConfig = {}) => ({
  color: [color],
  grid: {
    left: 0,
    right: 20,
    top: 44,
    bottom: 0,
  },
  legend: config.showLegend
    ? {
        top: 0,
        right: 8,
        icon: 'rect',
        itemWidth: 16,
        itemHeight: 4,
        data: [config.seriesName ?? '数据'],
      }
    : undefined,
  tooltip: getTooltip({ [config.seriesName ?? '数据']: unit }),
  xAxis: {
    ...timeValueXAxis,
  },
  yAxis: {
    type: 'value',
    name: unit,
    axisLine: hiddenAxisLine,
    axisTick: hiddenAxisTick,
  },
  series: [
    {
      name: config.seriesName ?? '数据',
      type: 'line',
      smooth: true,
      showSymbol: false,
      lineStyle: { width: config.lineWidth ?? 1 },
      areaStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: config.areaStartColor ?? color },
          { offset: 1, color: config.areaEndColor ?? 'rgba(0, 0, 0, 0)' },
        ]),
      },
      data: toTimedValues(data),
    },
  ],
});

export const getCloudOption = (data: Point[]) => ({
  ...getAreaLineOption(data, '#16DD8E', '%', {
    showLegend: true,
    seriesName: '综合云量',
    areaStartColor: 'rgba(22,221,142,0.3)',
    areaEndColor: 'rgba(22,221,142,0)',
    lineWidth: 2,
  }),
  grid: {
    left: 0,
    right: 20,
    top: 60,
    bottom: 0,
  },
  yAxis: {
    type: 'value',
    name: '%',
    min: 0,
    max: 100,
    nameTextStyle: unitNameTextStyle,
    axisLine: hiddenAxisLine,
    axisTick: hiddenAxisTick,
  },
});

export const getVisibleCloudOption = (data: { time: string; thick: number | null; thin: number | null }[]) => ({
  color: ['#2D8DFF', '#52E7FF'],
  grid: {
    left: 16,
    right: 20,
    top: 60,
    bottom: 0,
  },
  legend: {
    top: 0,
    right: 4,
    icon: 'rect',
    itemWidth: 16,
    itemHeight: 4,
    data: ['厚云', '薄云'],
  },
  tooltip: getTooltip({ 厚云: '%', 薄云: '%' }),
  xAxis: {
    ...timeValueXAxis,
  },
  yAxis: {
    type: 'value',
    name: '%',
    min: 0,
    max: 100,
    axisLine: hiddenAxisLine,
    axisTick: hiddenAxisTick,
  },
  series: [
    {
      name: '厚云',
      type: 'line',
      smooth: true,
      showSymbol: false,
      lineStyle: {
        width: 2,
        color: '#2D8DFF',
      },
      areaStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: 'rgba(45,141,255,0.3)' },
          { offset: 1, color: 'rgba(45,141,255,0)' },
        ]),
      },
      data: data.map(item => [parseMinuteOfDay(item.time), item.thick]),
    },
    {
      name: '薄云',
      type: 'line',
      smooth: true,
      showSymbol: false,
      lineStyle: {
        width: 2,
        color: '#52E7FF',
      },
      areaStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: 'rgba(82,231,255,0.3)' },
          { offset: 1, color: 'rgba(82,231,255,0)' },
        ]),
      },
      data: data.map(item => [parseMinuteOfDay(item.time), item.thin]),
    },
  ],
});

export const getForecastOption = (actual: Point[], predict: Point[], selectedDate?: Dayjs) => {
  const actualValues = toTimedValues(actual);
  const predictValues = toTimedValues(predict);
  const validValues = [...actualValues, ...predictValues].filter(
    (value): value is [number, number] => value[1] != null && Number.isFinite(value[1])
  );
  const maxValue = Math.max(...validValues.map(([, value]) => value), 0);
  const yAxisMax = maxValue > 0 ? Math.ceil(maxValue / 20) * 20 : undefined;

  // 判断是否是当天
  const isToday = selectedDate ? selectedDate.isSame(dayjs(), 'day') : true;

  // 只有当天才显示当前时间标记
  const nowMinuteOfDay = dayjs().hour() * 60 + dayjs().minute();
  const currentPoint =
    isToday &&
    actual.reduce<Point | undefined>((nearest, item) => {
      if (!nearest) {
        return item;
      }
      return Math.abs(parseMinuteOfDay(item.time) - nowMinuteOfDay) <
        Math.abs(parseMinuteOfDay(nearest.time) - nowMinuteOfDay)
        ? item
        : nearest;
    }, undefined);

  return {
    color: ['#16DD8E', '#FFCB47'],
    grid: { left: 0, right: 16, top: 36, bottom: 0 },
    legend: {
      top: 0,
      right: 8,
      itemWidth: 16,
      itemHeight: 4,
      data: [
        { name: '实际(历史)', icon: 'rect' },
        { name: '预测(未来)', icon: 'path://M0 0h4v4H0z M6 0h4v4H6z M12 0h4v4h-4z' },
      ],
    },
    tooltip: getTooltip({ '实际(历史)': 'MW', '预测(未来)': 'MW' }),
    xAxis: {
      ...timeValueXAxis,
    },
    yAxis: {
      type: 'value',
      name: 'MW',
      min: 0,
      max: yAxisMax,
      axisLine: hiddenAxisLine,
      axisTick: hiddenAxisTick,
    },
    series: [
      {
        name: '实际(历史)',
        type: 'line',
        smooth: true,
        showSymbol: false,
        lineStyle: {
          width: 2,
          color: '#16DD8E',
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(22,221,142,0.3)' },
            { offset: 1, color: 'rgba(22,221,142,0)' },
          ]),
        },
        markLine: {
          symbol: 'none',
          silent: true,
          lineStyle: {
            type: 'solid',
            color: 'rgba(0,251,250,0.5)',
            width: 1,
          },
          label: {
            show: false,
          },
          data: currentPoint ? [{ xAxis: parseMinuteOfDay(currentPoint.time) }] : [],
        },
        markPoint: {
          symbol: 'path://M0 0 L10 0 L5 6 Z',
          symbolSize: [10, 6],
          symbolOffset: [0, 0],
          silent: true,
          itemStyle: {
            color: '#276f73',
            borderWidth: 0,
          },
          label: {
            show: true,
            formatter: '当前时间',
            position: 'top',
            distance: 0,
            padding: [6, 8],
            color: 'rgba(255, 255, 255, 0.85)',
            fontSize: 12,
            backgroundColor: '#276f73',
            borderColor: '#276f73',
            borderWidth: 1,
          },
          data: currentPoint && yAxisMax ? [{ coord: [parseMinuteOfDay(currentPoint.time), yAxisMax] }] : [],
        },
        data: actualValues,
      },
      {
        name: '预测(未来)',
        type: 'line',
        smooth: true,
        showSymbol: false,
        lineStyle: { type: 'dashed', width: 2, color: '#FFCB47' },
        data: predictValues,
        markArea: {
          silent: true,
          itemStyle: { color: 'rgba(233, 181, 65, 0.24)' },
          data: predictValues.some(([, value]) => value != null)
            ? [[{ xAxis: parseMinuteOfDay('14:00') }, { xAxis: parseMinuteOfDay('16:00') }]]
            : [],
        },
        markPoint: {
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: '#ffd450',
            borderColor: 'rgba(255, 255, 255, 0.8)',
            borderWidth: 1,
          },
          label: {
            show: true,
            formatter: '未来30min\n下降≥20%',
            position: 'right',
            distance: 8,
            padding: [7, 10],
            color: 'var(--mx-text-base-color)',
            fontSize: 12,
            lineHeight: 18,
            backgroundColor: 'rgba(255, 203, 71, 0.3)',
            borderColor: 'rgba(255, 212, 80, 0.24)',
            borderWidth: 1,
          },
          data: (() => {
            const point = predict.find(item => item.time === '16:00');
            return point?.value != null ? [{ coord: [parseMinuteOfDay('16:00'), point.value] }] : [];
          })(),
        },
      },
    ],
  };
};

export const getWeatherOption = (ghi: Point[], temperature: Point[]) => ({
  color: ['#52E7FF', '#FFCB47'],
  grid: { left: 0, right: 0, top: 56, bottom: 0 },
  legend: {
    top: 0,
    right: 6,
    icon: 'rect',
    itemWidth: 16,
    itemHeight: 4,
    data: ['水平总辐射', '环境温度'],
  },
  tooltip: getTooltip({ 水平总辐射: 'W/m²', 环境温度: '℃' }),
  xAxis: {
    ...timeValueXAxis,
  },
  yAxis: [
    {
      type: 'value',
      name: 'W/m²',
      axisLine: hiddenAxisLine,
      axisTick: hiddenAxisTick,
    },
    {
      type: 'value',
      name: '℃',
      min: 0,
      max: 40,
      axisLine: hiddenAxisLine,
      axisTick: hiddenAxisTick,
      splitLine: { show: false },
    },
  ],
  series: [
    {
      name: '水平总辐射',
      type: 'line',
      smooth: true,
      showSymbol: false,
      lineStyle: {
        width: 2,
        color: '#52E7FF',
      },
      areaStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: 'rgba(82,231,255,0.3)' },
          { offset: 1, color: 'rgba(82,231,255,0)' },
        ]),
      },
      data: toTimedValues(ghi),
    },
    {
      name: '环境温度',
      yAxisIndex: 1,
      type: 'line',
      smooth: true,
      showSymbol: false,
      lineStyle: {
        width: 2,
        color: '#FFCB47',
      },
      areaStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: 'rgba(255,203,71,0.3)' },
          { offset: 1, color: 'rgba(255,203,71,0)' },
        ]),
      },
      data: toTimedValues(temperature),
    },
  ],
});
